/**
 * Share API Service
 *
 * Service functions for managing shareable LLM scan reports.
 * Uses apiClient for authenticated endpoints and publicApiClient for public endpoints.
 */

import { apiClient, publicApiClient } from "./api";
import type {
  ShareCreateRequest,
  ShareRead,
  ShareTokenMetadata,
  ShareAccessRequest,
  LLMScanSummary,
  PaginatedResponse,
  LLMScanResult,
  CloneConfigResponse,
} from "@/types/llm";

const API_PREFIX = "/api/v1/redteam";
const PUBLIC_API_PREFIX = "/api/v1/public";

/**
 * Create a shareable link for an LLM scan.
 * Requires authentication.
 */
export async function createShare(scanUuid: string, data: ShareCreateRequest): Promise<ShareRead> {
  return apiClient.post<ShareRead>(`${API_PREFIX}/scan/${scanUuid}/share`, data);
}

/**
 * List all active shares for a scan.
 * Requires authentication.
 */
export async function listShares(scanUuid: string): Promise<ShareRead[]> {
  return apiClient.get<ShareRead[]>(`${API_PREFIX}/scan/${scanUuid}/shares`);
}

/**
 * Revoke a share link.
 * Requires authentication.
 */
export async function revokeShare(shareUuid: string): Promise<void> {
  return apiClient.delete(`${API_PREFIX}/share/${shareUuid}`);
}

/**
 * Validate a share token (public endpoint, no auth required).
 * Uses publicApiClient to avoid sending auth headers.
 */
export async function validateShareToken(token: string): Promise<ShareTokenMetadata> {
  return publicApiClient.get<ShareTokenMetadata>(`${PUBLIC_API_PREFIX}/share/${token}`);
}

/**
 * Access a shared scan summary (public endpoint, no auth required).
 * Uses publicApiClient to avoid sending auth headers.
 */
export async function accessShare(token: string, password?: string): Promise<LLMScanSummary> {
  return publicApiClient.post<LLMScanSummary>(`${PUBLIC_API_PREFIX}/share/${token}/access`, {
    password: password || null,
  } as ShareAccessRequest);
}

/**
 * Get results for a shared scan (public endpoint, no auth required).
 * Uses publicApiClient to avoid sending auth headers.
 * Password is sent in POST body to avoid logging in URLs.
 * Supports server-side filtering by status, severity, plugin_id, and search query.
 */
export async function getSharedScanResults(
  token: string,
  password?: string,
  page: number = 1,
  itemsPerPage: number = 50,
  status?: string,
  severity?: string,
  pluginId?: string,
  search?: string
): Promise<PaginatedResponse<LLMScanResult>> {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("items_per_page", itemsPerPage.toString());
  if (status) params.append("status", status);
  if (severity) params.append("severity", severity);
  if (pluginId) params.append("plugin_id", pluginId);
  if (search) params.append("search", search);

  const queryString = params.toString();
  const url = `${PUBLIC_API_PREFIX}/share/${token}/results${queryString ? `?${queryString}` : ""}`;

  // Send password in request body via POST to avoid logging in URLs
  return publicApiClient.post<PaginatedResponse<LLMScanResult>>(url, {
    password: password || null,
  } as ShareAccessRequest);
}

/**
 * Get safe config fields for cloning a shared scan (public endpoint, no auth required).
 * Uses publicApiClient to avoid sending auth headers.
 *
 * Returns only non-sensitive configuration (prompt, plugins, strategies)
 * that can be pre-filled in the DemoFlowPage form.
 *
 * @throws Error with status 403 if share is password-protected
 * @throws Error with status 401 if share is expired or revoked
 * @throws Error with status 404 if token or scan not found
 */
export async function getCloneConfig(token: string): Promise<CloneConfigResponse> {
  return publicApiClient.get<CloneConfigResponse>(`${PUBLIC_API_PREFIX}/share/${token}/clone-config`);
}
